import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import showToast from "../components/Toast/Toaster";
import AxiosInstance from "../Axiosinstance";
import sendSwal from "../components/Swal/sendSwal";

const initialState = {
  cartCount: 0,
  cartData: [],
};
const baseUrl = process.env.REACT_APP_BASE_API;

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCart: (state, action) => {
      state.cartCount = action.payload.count;
      state.cartData = action.payload.data;
    },
    removeCart: (state) => {
      state.cartCount = 0;
      state.cartData = [];
    },
    addItemToCart: (state, action) => {
      // Check if item already exists to avoid duplicates
      const existingItem = state.cartData.find(
        (item) => item.AddToCartId === action.payload.AddToCartId
      );
      if (!existingItem) {
        state.cartData.push(action.payload); // Optimistic update
        state.cartCount += 1;
      }
    },
    removeItemFromCart: (state, action) => {
      const initialLength = state.cartData.length;
      state.cartData = state.cartData.filter(
        (item) => item.AddToCartId !== action.payload
      );
      // Only decrement if an item was actually removed
      if (state.cartData.length < initialLength) {
        state.cartCount -= 1;
      }
    },
  },
});

export const { setCart, removeCart, addItemToCart, removeItemFromCart } =
  cartSlice.actions;

export const fetchCartCount = (userId) => async (dispatch) => {
  try {
    // Use tempUserId if no userId provided
    let tempUserId = userId;
    if (!tempUserId) {
      tempUserId = localStorage.getItem("tempUserId");
      if (!tempUserId) {
        // No temp user ID, set empty cart
        dispatch(setCart({ count: 0, data: [] }));
        return;
      }
    }

    const res = await AxiosInstance.get(
      `${baseUrl}/cart/cart?userId=${tempUserId}`
    );

    if (res.data.statusCode === 200) {
      dispatch(setCart({ count: res.data.TotalCount, data: res.data.data }));
    } else if (res.data.statusCode === 204) {
      // No cart items found - this is normal, not an error
      dispatch(setCart({ count: 0, data: [] }));
    } else {
      dispatch(setCart({ count: 0, data: [] }));
    }
  } catch (error) {
    console.error("Cart fetch error:", error);
    dispatch(setCart({ count: 0, data: [] }));
  }
};

// export const addToCart = (item, userId, shouldShowToast, navigate) => async (dispatch) => {
//   try {
//     const token = localStorage.getItem("Token");
//     const payload = { ...item, UserId: userId }; // Create payload
//     console.log(payload, "addToCart request body"); // Log the request body
//     const response = await AxiosInstance.post(
//       `${baseUrl}/cart/addtocart`,
//       payload
//     );
//     console.log(response, "addToCart API response"); // Debug log
//     if (response.data.statusCode === 200) {
//       dispatch(addItemToCart(response.data.data)); // Add item optimistically
//       dispatch(fetchCartCount(userId));
//       if (shouldShowToast) {
//         showToast.success(response.data.message);
//       }
//     } else if (response.data.statusCode === 202) {
//       if (shouldShowToast) {
//         showToast.warning(response.data.message);
//       }
//     } else if (response.data.statusCode === 203) {
//       if (shouldShowToast) {
//         showToast.warning(response.data.message);
//       }
//     } else if (response.data.statusCode === 401) {
//       showToast.error("Your Session Expired. Please Login Again");
//       navigate("/login");
//     } else {
//       console.error("Failed to add item:", response.data.message);
//       showToast.warning("Your Session Expired. Please Login Again");
//     }
//   } catch (error) {
//     showToast.error("Your Session Expired. Please Login Again");
//     navigate("/login");
//   }
// };

export const addToCart = (item, userId, shouldShowToast, navigate) => async (dispatch) => {
  try {
    // Generate a temporary userId if user is not logged in
    let tempUserId = userId;
    if (!tempUserId) {
      // Check if we already have a temporary user ID in localStorage
      tempUserId = localStorage.getItem("tempUserId");
      if (!tempUserId) {
        // Generate a new temporary user ID
        tempUserId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem("tempUserId", tempUserId);
      }
    }

    // Ensure UserId is included in the request body
    const cartItem = {
      ...item,
      UserId: tempUserId,
    };

    // Make the API call without authentication
    const response = await AxiosInstance.post(
      `${baseUrl}/cart/addtocart?UserId=${tempUserId}`,
      cartItem
    );

    if (response.data.statusCode === 200) {
      dispatch(addItemToCart(response.data.data));
      dispatch(fetchCartCount(tempUserId));
      if (shouldShowToast) {
        showToast.success(response.data.message);
      }
    } else if (response.data.statusCode === 202) {
      if (shouldShowToast) {
        showToast.warning(response.data.message);
      }
    } else if (response.data.statusCode === 203) {
      if (shouldShowToast) {
        showToast.warning(response.data.message);
      }
    } else if (response.data.statusCode === 400) {
      // Handle specific 400 errors
      if (shouldShowToast) {
        showToast.error(response.data.message || "Invalid cart item data.");
      }
    } else {
      if (shouldShowToast) {
        showToast.error(response.data.message || "Failed to add item to cart.");
      }
    }
  } catch (error) {
    console.error("Add to cart error:", error.response?.data || error.message);
    if (shouldShowToast) {
      showToast.error(error.response?.data?.message || "An error occurred while adding to cart.");
    }
  }
};

// export const removeFromCart = (AddToCartId, userId) => async (dispatch) => {
//   // Show confirmation alert before deleting
//   const willDelete = await sendSwal();

//   if (willDelete) {
//     try {
//       const response = await AxiosInstance.delete(
//         `${baseUrl}/cart/updatecart/${AddToCartId}`
//       );

//       if (response.data.statusCode === 200) {
//         dispatch(removeItemFromCart(AddToCartId));
//         dispatch(fetchCartCount(userId));
//         showToast.success("Item Removed Successfully.");
//       } else {
//         showToast.error("Failed to remove item.");
//       }
//     } catch (error) {
//       console.error("Remove from cart error:", error);
//       showToast.error("Something went wrong.");
//     }
//   }
// };

export const removeFromCart = (AddToCartId, id, userId, navigate) => async (dispatch, getState) => {
  const willDelete = await sendSwal();

  if (willDelete) {
    // Optimistically remove the item from the UI
    const prevCartData = getState().cart.cartData;
    const updatedCartData = prevCartData.filter((item) => item.AddToCartId !== AddToCartId);
    dispatch(removeItemFromCart(AddToCartId));

    try {
      // Get current user ID for cart count refresh
      const realUserId = localStorage.getItem("UserId");
      const tempUserId = localStorage.getItem("tempUserId");
      const currentUserId = realUserId || tempUserId;

      const response = await AxiosInstance.delete(
        `${baseUrl}/cart/updatecart/${AddToCartId}`
      );

      if (response.data.statusCode === 200) {
        showToast.success("Item Removed Successfully.");
        // Refresh cart count with current user ID
        if (currentUserId) {
          dispatch(fetchCartCount(currentUserId));
        }
      } else {
        showToast.error("Failed to remove item.");
        dispatch(setCart({ count: prevCartData.length, data: prevCartData })); // Rollback on failure
      }
    } catch (error) {
      console.error("Remove from cart error:", error);
      showToast.error(error.response?.data?.message || "An error occurred while removing from cart.");
      dispatch(setCart({ count: prevCartData.length, data: prevCartData })); // Rollback on error
    }
  }
};




// Function to transfer temporary cart to logged-in user
export const transferTempCartToUser = (tempUserId, realUserId) => async (dispatch) => {
  try {
    console.log("Transferring cart from tempUserId:", tempUserId, "to realUserId:", realUserId);
    
    if (!tempUserId || !realUserId) {
      console.log("Missing tempUserId or realUserId");
      return;
    }

    // Call API to transfer cart items from temp user to real user
    const response = await AxiosInstance.post(
      `${baseUrl}/cart/transfer-cart`,
      { tempUserId, realUserId }
    );

    console.log("Transfer cart response:", response.data);

    if (response.data.statusCode === 200) {
      // Fetch updated cart for the real user (silently)
      await dispatch(fetchCartCount(realUserId));
      console.log("Cart transferred successfully");
    }
  } catch (error) {
    console.error("Error transferring cart:", error);
  }
};

export default cartSlice.reducer;
